import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { AgencyMoveAction } from '@/components/admin/users/agency-move-action';
import { CognitoActions } from '@/components/admin/users/cognito-actions';
import { UserAssignments } from '@/components/admin/users/user-assignments';
import { UserDetail } from '@/components/admin/users/user-detail';
import { UserDeleteAction } from '@/components/admin/users/user-delete-action';
import { UserEdit } from '@/components/admin/users/user-edit/user-edit';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { meQueryOptions } from '@/hooks/data/use-auth';
import { useAdminUser } from '@/hooks/data/admin/use-users';
import { ROUTES } from '@/constants/routes';
import { loadNamespaces } from '@/lib/i18n/i18n';

const paramSchema = z.object({ userId: z.coerce.number().int().positive() });

export const Route = createFileRoute('/_authenticated/admin/users/$userId')({
  loader: () => loadNamespaces('settings'),
  staticData: { title: 'User' },
  parseParams: (params) => paramSchema.parse(params),
  component: AdminUserDetailPage,
});

function DetailSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-4 px-4 py-6 sm:px-8 @3xl:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton variant="card" key={i} className="h-48 rounded-xl" />
      ))}
    </div>
  );
}

function AdminUserDetailPage(): React.ReactElement {
  const { userId } = Route.useParams();
  const userQuery = useAdminUser(userId);
  const { data: me } = useQuery(meQueryOptions);

  if (userQuery.isLoading) {
    return (
      <main>
        <AdminPageHeader title="User" />
        <DetailSkeleton />
      </main>
    );
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <main>
        <AdminPageHeader
          title="User"
          actions={
            <Button variant="outline" asChild>
              <Link to={ROUTES.ADMIN_USERS}>Back</Link>
            </Button>
          }
        />
        <div className="px-4 py-12 text-center sm:px-8">
          <p className="text-fg-3 text-fs-13">
            Failed to load user. Please try again.
          </p>
        </div>
      </main>
    );
  }

  const user = userQuery.data;
  const isSelf = me?.id === user.id;

  return (
    <main>
      <AdminPageHeader
        title={user.email}
        description={
          user.organization_name
            ? `Organization · ${user.organization_name}`
            : undefined
        }
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to={ROUTES.ADMIN_USERS}>Back</Link>
            </Button>
            <AgencyMoveAction user={user} />
            <UserDeleteAction
              user={user}
              disabled={isSelf || user.role === 'SUPER_ADMIN'}
            />
          </div>
        }
      />
      <div className="grid gap-4 px-4 py-6 sm:px-8 @3xl:grid-cols-2">
        <UserDetail user={user} />
        <UserAssignments assignments={user.assignments} />
        <div className="flex flex-col gap-4">
          <UserEdit user={user} disabled={isSelf} />
          <CognitoActions user={user} disabled={isSelf} />
        </div>
      </div>
    </main>
  );
}
